<template>
  <b-container fluid>
    <ValidationObserver ref="productObserver" v-slot="{ invalid }">
      <form>
        <iq-card v-if="canUseAiConvert || aiReview || requiresAiAcknowledgment">
          <template v-slot:body>
            <!-- Sıfırdan onam yazmak korkutucu; hazır dosyası olan kullanıcıya kısayolu
                 ilk anda gösteriyoruz. Dönüştürene veya kapatana kadar durur; doluluğa
                 bağlamak ilk harfte kartın zıplayarak kaybolmasına yol açıyor. -->
            <!-- Düz div: Vue 2'de bir BİLEŞENE @click yazmak özel olay dinler, native
                 click'i değil (b-alert `click` emit etmiyor) ve kart tıklanamaz kalıyordu. -->
            <div v-if="showAiConvertPrompt" class="alert alert-light ai-prompt mb-0 d-flex align-items-center"
                 @click="openAiConvertModal()">
              <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" size="lg" class="ai-prompt__icon mr-3" />
              <div class="flex-grow-1">
                <strong>{{ $t('AiConvertPromptTitle') }}</strong>
                <div class="text-muted small">{{ $t('AiConvertPromptDescription') }}</div>
              </div>
              <b-button variant="primary" size="sm" class="mr-2">
                <font-awesome-icon icon="fa-solid fa-arrow-up-from-bracket" class="mr-1" />{{ $t('AiConvertPromptAction') }}
              </b-button>
              <font-awesome-icon class="close-icon" icon="fa-regular fa-xmark" :title="$t('Close')"
                                 @click.stop="aiPromptDismissed = true" />
            </div>

            <div v-else-if="canUseAiConvert" class="text-right">
              <b-button variant="outline-primary" size="sm" @click="openAiConvertModal()">
                <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" class="mr-1" />{{ $t('AiConvertButton') }}
              </b-button>
            </div>

            <b-alert v-if="aiReview" show variant="info" class="ai-review mt-3 mb-0">
              <div class="d-flex align-items-start">
                <div class="flex-grow-1">
                  <strong>{{ $t('AiConvertReviewTitle') }}</strong>
                  <div class="small">{{ $t('AiConvertReviewDescription') }}</div>
                </div>
                <b-badge :variant="confidenceVariant" class="mr-2">{{ confidenceLabel }}</b-badge>
                <font-awesome-icon class="close-icon" icon="fa-regular fa-xmark" :title="$t('Close')"
                                   @click="aiReview = null" />
              </div>

              <ul class="ai-review__items mt-2 mb-0">
                <!-- Engelleyici madde yönü yanlışsa danışan haksız yere kilitlenir;
                     bu yüzden diğer uyarıların üstünde. -->
                <li v-if="aiReview.blockerClauseCount > 0" class="text-danger">
                  {{ $t('AiConvertReviewBlockerCheck').replace('{count}', aiReview.blockerClauseCount) }}
                </li>
                <li v-if="aiReview.confidence < 0.5">{{ $t('AiConvertReviewLowConfidence') }}</li>
                <!-- Sadakat: BİLGİLENDİRME, kaydı engellemez. -->
                <li v-if="showGroundingWarning">
                  {{ $t(aiReview.validation.summaryKey)
                      .replace('{ungrounded}', aiReview.validation.ungroundedItemCount)
                      .replace('{checked}', aiReview.validation.checkedItemCount) }}
                  <ul>
                    <li v-for="(item, index) in groundingItems" :key="'aigi' + index" class="text-muted small">
                      <b v-if="item.sectionKey">{{ $t(item.sectionKey) }}:</b> {{ item.text }}
                    </li>
                  </ul>
                </li>
                <li v-for="(key, index) in aiReview.warningKeys" :key="'aiwk' + index">{{ $t(key) }}</li>
                <li v-for="(warning, index) in aiReview.warnings" :key="'aiw' + index">{{ warning }}</li>
              </ul>
            </b-alert>

            <!-- Sorumluluk onayı: AI taslağının İLK kaydında zorunlu. İnceleme bandından
                 AYRI duruyor çünkü band kapatılabiliyor; kutunun kapatılabilir bir alanın
                 içinde kalması kaydı kilitlerdi. -->
            <div v-if="requiresAiAcknowledgment" class="mt-3">
              <b-form-checkbox v-model="aiAcknowledgmentAccepted">
                {{ $t('AiConvertAcknowledgment') }}
              </b-form-checkbox>
              <small v-if="aiAcknowledgmentError" class="text-danger">
                {{ $t('AiConvertAcknowledgmentRequired') }}
              </small>
            </div>
          </template>
        </iq-card>
        <iq-card>
          <template v-slot:body>
            <b-row>
              <b-form-group class="col-md-6" :label="$t('Name')" label-for="name">
                <ValidationProvider :name="$t('Name')" rules="required" v-slot="{ errors }">
                  <b-form-input :class="(errors.length > 0 ? ' is-invalid' : '')" v-model="model.formName " type="text"></b-form-input>
                  <b-form-invalid-feedback> {{ $t('ValidationMessage.CannotBeEmpty') }} </b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>
              <b-form-group class="col-md-6" :label="$t('Language')" label-for="language">
                <ValidationProvider :name="$t('Language')" rules="required" v-slot="{ errors }">
                  <v-select transition="" v-model="model.culture"
                            :reduce="s => s.culture" label="name"
                            :options="cultures">
                  <template v-slot:no-options> {{$t('NoMatchingOptionsMessage')}} </template> </v-select>
                  <b-form-invalid-feedback> {{ $t('ValidationMessage.CannotBeEmpty') }} </b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>
            </b-row>
            <b-row>
              <b-form-group class="col-md-6" :label="$t('Operation')" label-for="operation">
                <b-form-input v-model="model.treatmentName" type="text"></b-form-input>
              </b-form-group>
              <!-- Opsiyonel. Boş bırakılırsa takip/indirme adı form adına düşer. -->
              <b-form-group class="col-md-6" :label="$t('CustomConsentFormReportName')" label-for="reportName"
                            :description="$t('CustomConsentFormReportNameHint')">
                <b-form-input v-model="model.reportName" type="text"></b-form-input>
              </b-form-group>
            </b-row>
            <b-row>
              <b-form-group class="col-md-12" :label="$t('Description')" label-for="description">
                <vue-editor id="editorDescription" v-model="model.descriptionHtml" :editorToolbar="customToolbar"></vue-editor>
              </b-form-group>
            </b-row>
          </template>
        </iq-card>
        <iq-card>
          <template v-slot:body>
            <b-row>
              <b-form-group class="col-md-6">
                <b-form-checkbox v-model="model.showClauses" switch inline size="lg">
                  <span style="font-size:15px !important; vertical-align:middle !important">{{ $t('ShowClauses') }}</span>
                </b-form-checkbox>
              </b-form-group>
              <div v-if="model.showClauses" class="col-md-6" style="text-align:end;">
                <button type="button"
                        class="btn btn-primary btn-sm"
                        @click="addClause()">
                  <font-awesome-icon icon="fa-solid fa-plus" size="lg" />
                  {{$t('AddNewLine')}}
                </button>
              </div>
            </b-row>
            <br v-if="model.showClauses" />
            <b-row v-if="model.showClauses">
              <b-form-group class="col-md-6" :label="$t('ConsentType')" label-for="consentType">
                <ValidationProvider :name="$t('ConsentType')" rules="required" v-slot="{ errors }">
                  <v-select transition="" v-model="model.clauseConsentType"
                            :reduce="s => s.id" label="name"
                            :options="consentTypeList">
                  <template v-slot:no-options> {{$t('NoMatchingOptionsMessage')}} </template> </v-select>
                  <b-form-invalid-feedback> {{ $t('ValidationMessage.CannotBeEmpty') }} </b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>
            </b-row>
            <template v-if="model.showClauses && model.clauses">
              <b-row v-for="(clause, index) in model.clauses" :key="index" class="align-items-start mb-2">
                <b-form-group class="col-md-7 mb-0">
                  <b-form-input v-model="clause.text" :placeholder="$t('ClauseText')"></b-form-input>
                </b-form-group>
                <div class="col-md-4">
                  <b-form-checkbox v-model="clause.isBlockerQuestion" inline size="sm"
                                   @change="onBlockerQuestionChange(clause, $event)">
                    <span style="font-size:12px;">{{ $t('BlockerQuestion') }}</span>
                  </b-form-checkbox>
                  <!-- Açıklama isteği engelleyicilikten BAĞIMSIZ: engellemeyen bir
                       maddede de "Evet" cevabının detayı istenebiliyor. -->
                  <b-form-checkbox :checked="hasDetailRequest(clause)" inline size="sm"
                                   @change="toggleDetailRequest(clause)">
                    <span style="font-size:12px;">{{ $t('ClauseDetailRequest') }}</span>
                  </b-form-checkbox>
                  <v-select v-if="clause.isBlockerQuestion"
                            transition=""
                            v-model="clause.blockerAnswerType"
                            :reduce="s => s.id"
                            label="name"
                            :options="blockerAnswerTypeList"
                            :placeholder="$t('SelectBlockerAnswer')"
                            style="font-size:12px; margin-top:4px;">
                    <template v-slot:no-options> {{$t('NoMatchingOptionsMessage')}} </template>
                  </v-select>
                  <template v-if="hasDetailRequest(clause)">
                    <v-select transition=""
                              v-model="clause.detailAnswerType"
                              :reduce="s => s.id"
                              label="name"
                              :clearable="false"
                              :options="detailAnswerTypeList"
                              style="font-size:12px; margin-top:4px;">
                      <template v-slot:no-options> {{$t('NoMatchingOptionsMessage')}} </template>
                    </v-select>
                    <b-form-input v-model="clause.detailLabel"
                                  maxlength="250"
                                  size="sm"
                                  style="margin-top:4px;"
                                  :placeholder="$t('ClauseDetailLabelPlaceholder')"></b-form-input>
                  </template>
                </div>
                <div class="ccf-controls">
                  <div class="btn-group-vertical btn-group-sm ccf-controls__move">
                    <button type="button"
                            :disabled="index==0"
                            class="btn btn-brand"
                            @click="reorderClauses(clause, '+')">
                      <font-awesome-icon icon="fa-regular fa-angle-up" />
                    </button>
                    <button type="button"
                            :disabled="index==model.clauses.length-1"
                            class="btn btn-brand"
                            @click="reorderClauses(clause, '-')">
                      <font-awesome-icon icon="fa-regular fa-angle-down" />
                    </button>
                  </div>
                  <button type="button" class="btn btn-sm ccf-controls__remove"
                          :title="$t('Remove')" @click="removeClause(clause)">
                    <font-awesome-icon icon="fa-regular fa-xmark" />
                  </button>
                </div>
              </b-row>
            </template>
            <b-row v-if="model.showClauses && hasBlockerQuestions" class="mt-3">
              <b-form-group class="col-md-12">
                <label class="font-weight-bold">{{ $t('BlockerMessage') }} <span class="text-danger">*</span></label>
                <b-form-textarea v-model="model.blockerMessage"
                                 rows="3"
                                 :state="blockerMessageState"
                                 :placeholder="$t('BlockerMessageHint')">
                </b-form-textarea>
                <b-form-invalid-feedback>{{ $t('ValidationMessage.CannotBeEmpty') }}</b-form-invalid-feedback>
              </b-form-group>
            </b-row>
          </template>
        </iq-card>
        <iq-card>
          <template v-slot:body>
            <b-row>
              <b-form-group class="col-md-6">
                <b-form-checkbox v-model="model.showSideEffects" switch inline size="lg">
                  <span style="font-size:15px !important; vertical-align:middle !important">{{ $t('ShowSideEffects') }}</span>
                </b-form-checkbox>
              </b-form-group>
              <div v-if="model.showSideEffects" class="col-md-6" style="text-align:end;">
                <button type="button"
                        class="btn btn-primary btn-sm"
                        @click="addSideEffect()">
                  <font-awesome-icon icon="fa-solid fa-plus" size="lg" />
                  {{$t('AddNewLine')}}
                </button>
              </div>
            </b-row>
            <br v-if="model.showSideEffects" />
            <template v-if="model.showSideEffects && model.sideEffects">
              <b-row v-for="(sideEffect, index) in model.sideEffects" :key="index" class="align-items-center mb-2">
                <b-form-group class="col-md-8 mb-0">
                  <b-form-input v-model="sideEffect.text"></b-form-input>
                </b-form-group>
                <div class="ccf-controls">
                  <div class="btn-group-vertical btn-group-sm ccf-controls__move">
                    <button type="button"
                            :disabled="index==0"
                            class="btn btn-brand"
                            @click="reorderSideEffects(sideEffect, '+')">
                      <font-awesome-icon icon="fa-regular fa-angle-up" />
                    </button>
                    <button type="button"
                            :disabled="index==model.sideEffects.length-1"
                            class="btn btn-brand"
                            @click="reorderSideEffects(sideEffect, '-')">
                      <font-awesome-icon icon="fa-regular fa-angle-down" />
                    </button>
                  </div>
                  <button type="button" class="btn btn-sm ccf-controls__remove"
                          :title="$t('Remove')" @click="removeSideEffect(sideEffect)">
                    <font-awesome-icon icon="fa-regular fa-xmark" />
                  </button>
                </div>
              </b-row>
            </template>
          </template>
        </iq-card>
        <iq-card>
          <template v-slot:body>
            <b-row>
              <b-form-group class="col-md-6">
                <b-form-checkbox v-model="model.showConsiderationsBeforeTreatment" switch inline size="lg">
                  <span style="font-size:15px !important; vertical-align:middle !important">{{ $t('ShowConsiderationsBeforeTreatment') }}</span>
                </b-form-checkbox>
              </b-form-group>
              <div v-if="model.showConsiderationsBeforeTreatment" class="col-md-6" style="text-align:end;">
                <button type="button"
                        class="btn btn-primary btn-sm"
                        @click="addConsiderationBeforeTreatment()">
                  <font-awesome-icon icon="fa-solid fa-plus" size="lg" />
                  {{$t('AddNewLine')}}
                </button>
              </div>
            </b-row>
            <br v-if="model.showConsiderationsBeforeTreatment" />
            <template v-if="model.showConsiderationsBeforeTreatment && model.considerationsBeforeTreatment">
              <b-row v-for="(beforeTreatment, index) in model.considerationsBeforeTreatment" :key="index" class="align-items-center mb-2">
                <b-form-group class="col-md-8 mb-0">
                  <b-form-input v-model="beforeTreatment.text"></b-form-input>
                </b-form-group>
                <div class="ccf-controls">
                  <div class="btn-group-vertical btn-group-sm ccf-controls__move">
                    <button type="button"
                            :disabled="index==0"
                            class="btn btn-brand"
                            @click="reorderConsiderationsBeforeTreatment(beforeTreatment, '+')">
                      <font-awesome-icon icon="fa-regular fa-angle-up" />
                    </button>
                    <button type="button"
                            :disabled="index==model.considerationsBeforeTreatment.length-1"
                            class="btn btn-brand"
                            @click="reorderConsiderationsBeforeTreatment(beforeTreatment, '-')">
                      <font-awesome-icon icon="fa-regular fa-angle-down" />
                    </button>
                  </div>
                  <button type="button" class="btn btn-sm ccf-controls__remove"
                          :title="$t('Remove')" @click="removeConsiderationBeforeTreatment(beforeTreatment)">
                    <font-awesome-icon icon="fa-regular fa-xmark" />
                  </button>
                </div>
              </b-row>
            </template>
          </template>
        </iq-card>
        <iq-card>
          <template v-slot:body>
            <b-row>
              <b-form-group class="col-md-6">
                <b-form-checkbox v-model="model.showConsiderationsAfterTreatment" switch inline size="lg">
                  <span style="font-size:15px !important; vertical-align:middle !important">{{ $t('ShowConsiderationsAfterTreatment') }}</span>
                </b-form-checkbox>
              </b-form-group>
              <div v-if="model.showConsiderationsAfterTreatment" class="col-md-6" style="text-align:end;">
                <button type="button"
                        class="btn btn-primary btn-sm"
                        @click="addConsiderationAfterTreatment()">
                  <font-awesome-icon icon="fa-solid fa-plus" size="lg" />
                  {{$t('AddNewLine')}}
                </button>
              </div>
            </b-row>
            <br v-if="model.showConsiderationsAfterTreatment" />
            <template v-if="model.showConsiderationsAfterTreatment && model.considerationsAfterTreatment">
              <b-row v-for="(afterTreatment, index) in model.considerationsAfterTreatment" :key="index" class="align-items-center mb-2">
                <b-form-group class="col-md-8 mb-0">
                  <b-form-input v-model="afterTreatment.text"></b-form-input>
                </b-form-group>
                <div class="ccf-controls">
                  <div class="btn-group-vertical btn-group-sm ccf-controls__move">
                    <button type="button"
                            :disabled="index==0"
                            class="btn btn-brand"
                            @click="reorderConsiderationsAfterTreatment(afterTreatment, '+')">
                      <font-awesome-icon icon="fa-regular fa-angle-up" />
                    </button>
                    <button type="button"
                            :disabled="index==model.considerationsAfterTreatment.length-1"
                            class="btn btn-brand"
                            @click="reorderConsiderationsAfterTreatment(afterTreatment, '-')">
                      <font-awesome-icon icon="fa-regular fa-angle-down" />
                    </button>
                  </div>
                  <button type="button" class="btn btn-sm ccf-controls__remove"
                          :title="$t('Remove')" @click="removeConsiderationAfterTreatment(afterTreatment)">
                    <font-awesome-icon icon="fa-regular fa-xmark" />
                  </button>
                </div>
              </b-row>
            </template>
          </template>
        </iq-card>
        <iq-card>
          <template v-slot:body>
            <h5 class="mb-3">{{ $t('DigitalSignaturePreferences') }}</h5>
            <b-row>
              <b-form-group class="col-md-6">
                <b-form-checkbox v-model="model.showSignatureAtEnd" switch inline size="lg">
                  <span style="font-size:15px !important; vertical-align:middle !important">{{ $t('ShowSignatureAtEnd') }}</span>
                </b-form-checkbox>
              </b-form-group>
            </b-row>
            <!-- Danışan imzası listede yok, her zaman basılır (onamın kendisi). Doktor ve
                 tanık forma göre değişiyor: KVKK gibi onamlarda doktor imzası istenmiyor,
                 bazı onamlarda hukuken tanık da gerekiyor. Bayraklar ÇIKTIDA hangi sütunun
                 basılacağını söyler; ekranda imza alınmasını zorunlu KILMAZ. -->
            <b-row>
              <b-form-group class="col-md-12" :label="$t('SignatureAreas')"
                            :description="$t('SignatureAreasHint')">
                <b-form-checkbox v-model="model.showDoctorSignature" switch inline size="lg">
                  <span style="font-size:15px !important; vertical-align:middle !important">{{ $t('ResponsibleDoctor') }}</span>
                </b-form-checkbox>
                <b-form-checkbox v-model="model.showWitnessSignature" switch inline size="lg">
                  <span style="font-size:15px !important; vertical-align:middle !important">{{ $t('Witness') }}</span>
                </b-form-checkbox>
              </b-form-group>
            </b-row>
          </template>
        </iq-card>
        <iq-card>
          <template v-slot:body>
            <b-row>
              <b-form-group class="col-md-12" :label="$t('Footnote')" label-for="footnote">
                <vue-editor id="footnote" v-model="model.asteriskClarificationHtml" :editorToolbar="customToolbar"></vue-editor>
              </b-form-group>
            </b-row>
            <b-row>
              <b-form-group class="col-md-12" :label="$t('ConsentText')" label-for="consentText">
                <vue-editor id="consentText" v-model="model.consentText" :editorToolbar="customToolbar"></vue-editor>
              </b-form-group>
            </b-row>
          </template>
        </iq-card>
        <modal-footer-button :set-is-submitting="isSubmitting"
                             @submit="submitCustomConsentForm"></modal-footer-button>
      </form>
    </ValidationObserver>

    <CustomConsentFormAiConvertModal ref="aiConvertModal"
                                     :culture="model.culture"
                                     :has-existing-data="hasExistingFormData"
                                     @converted="applyAiConversion" />
  </b-container>

</template>
<script>

  import ModalFooterButton from '../../../components/shared/ModalFooterButton';
  import CustomConsentFormAiConvertModal from '../../../components/customConsentForm/CustomConsentFormAiConvertModal';
  import commonService from '../../../services/common';
  import consentFormService from '../../../services/customerConsentForm';
  import '../../../plugins/vue-editor';


  export default {
    name: 'CustomConsentForm',
    components: { ModalFooterButton, CustomConsentFormAiConvertModal },
    props: { customConsentFormId: String },
    data() {
      return {
        model: {
          id: '',
          formName: '',
          // Opsiyonel takip/indirme adı; boşsa backend formName'e düşer.
          reportName: '',
          treatmentName: '',
          culture: '',
          // Çıktıda hangi imza sütunlarının basılacağı. Varsayılan açık: bu alanlar
          // eklenmeden önce şablon üç sütunu her zaman basıyordu.
          showDoctorSignature: true,
          showWitnessSignature: true,
          // AI kökeni; taslak dönüştürmeden geldiğinde applyAiConversion doldurur.
          aiGenerated: false,
          aiCallLogId: null,
          showClauses: false,
          clauseConsentType: null,
          clauses: [],
          descriptionHtml: '',
          showSideEffects: false,
          sideEffects: [],
          showConsiderationsBeforeTreatment: false,
          considerationsBeforeTreatment: [],
          showConsiderationsAfterTreatment: false,
          considerationsAfterTreatment: [],
          consentText: '',
          asteriskClarificationHtml: '',
          showSignatureAtEnd: false,
          blockerMessage: ''
        },
        cultures: [],

        // Converter servisi yapılandırılmamış ortamlarda buton hiç gösterilmez.
        aiConvertAvailable: false,
        aiPromptDismissed: false,
        aiReview: null,
        aiAcknowledgmentAccepted: false,
        aiAcknowledgmentError: false,

        customToolbar: [
          [{ header: [false, 1, 2, 3, 4, 5, 6] }],
          ["bold", "italic", "underline", "strike"], // toggled buttons
          [
            { align: "" },
            { align: "center" },
            { align: "right" },
            { align: "justify" }
          ],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          ["clean"] // remove formatting button
        ],

        isSubmitting: false
      }
    },
    methods: {
      getAiConvertAvailability() {
        consentFormService.getConvertAvailability()
          .then(response => { this.aiConvertAvailable = !!response?.isAvailable; })
          .catch(() => { this.aiConvertAvailable = false; });
      },
      openAiConvertModal() {
        this.$refs.aiConvertModal?.open();
      },
      applyAiConversion(result) {
        if (!result || !result.form) {
          this.$toastr.error(this.$t('AiConvertFailed'));
          return;
        }

        // Tüm alanlar tek atamayla yazılır: show* watcher'ları listeler dolduktan sonra
        // tetiklenir, böylece boş madde satırı eklenmez.
        this.model = Object.assign({}, this.model, result.form, {
          id: this.model.id,
          aiGenerated: true,
          // Ölçüm satırı yazılamamış olabilir; kayıt bu yüzden engellenmez.
          aiCallLogId: result.aiCallLogId || null
        });

        // Yeni taslak = yeni onay. Önceki dönüştürme için verilmiş onay taşınmaz.
        this.aiAcknowledgmentAccepted = false;
        this.aiAcknowledgmentError = false;

        this.aiReview = {
          warnings: result.warnings || [],
          warningKeys: result.warningKeys || [],
          confidence: result.confidence || 0,
          blockerClauseCount: result.blockerClauseCount || 0,
          // Eski converter sürümü bu alanı göndermezse band sadakat satırı basmaz.
          validation: result.validation || null
        };

        this.$toastr.success(this.$t('AiConvertSuccess'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      /** Yön 1/-1 ise açıklama isteniyor; null/undefined ise istenmiyor. */
      hasDetailRequest(clause) {
        return clause.detailAnswerType === 1 || clause.detailAnswerType === -1;
      },
      toggleDetailRequest(clause) {
        if (this.hasDetailRequest(clause)) {
          // Kapatılan alanın başlığı kayıtta kalmasın.
          this.$set(clause, 'detailAnswerType', null);
          this.$set(clause, 'detailLabel', '');
          return;
        }

        // En sık kullanım "Evet ise açıkla"; yön ikinci satırdan değiştirilebilir.
        this.$set(clause, 'detailAnswerType', 1);
      },
      onBlockerQuestionChange(clause, isBlockerQuestion) {
        // Engelleyici kapatıldığında yön alanı temizlenir; aksi halde kayıtta gizli
        // bir blockerAnswerType kalıyordu.
        if (!isBlockerQuestion) {
          clause.blockerAnswerType = null;
        }
      },
      getCustomConsentForm() {
        consentFormService.getConsentForm(this.model.id)
          .then(response => {
            this.model = response;
            this.model.clauseConsentType = response.clauseConsentType == 0 ? null : response.clauseConsentType;
          });
      },
      async submitCustomConsentForm() {
        const isValid = await this.$refs.productObserver.validate();
        if (!isValid) {
          this.$toastr.error(this.$t("ValidationMessage.EmptyAndValidateError"));
          return;
        }
        if (this.hasBlockerQuestions && (!this.model.blockerMessage || this.model.blockerMessage.trim() === '')) {
          this.$toastr.error(this.$t("ValidationMessage.CannotBeEmpty"));
          return;
        }
        // AI taslağının İLK kaydında sorumluluk onayı zorunlu. Sunucuda da kapı var
        // (CustomConsentFormAiOriginMapper.TryApplyToNewForm); burası kullanıcıya
        // sebebini söyleyen taraf.
        this.aiAcknowledgmentError = this.requiresAiAcknowledgment && !this.aiAcknowledgmentAccepted;
        if (this.aiAcknowledgmentError) {
          this.$toastr.error(this.$t("AiConvertAcknowledgmentRequired"));
          return;
        }
        let url = "/api/CustomConsentForm/New";
        if (this.model.id && this.model.id.length > 0)
          url = "/api/CustomConsentForm/Edit"

        this.isSubmitting = true;

        // Onay bayrağı modelde tutulmaz: kaydedilen şey personelin o anki beyanı.
        const payload = Object.assign({}, this.model, {
          aiAcknowledgmentAccepted: this.aiAcknowledgmentAccepted
        });

        consentFormService.submitConsentForm(url, payload).then((response) => {
          this.isSubmitting = false;
          if (response) {
            this.$toastr.success(this.$t("Success"));
            this.$router.push({ name: 'customConsentForm.list' });
          }
          else {
            this.$toastr.error(this.$t("Error"));
          }
        }).finally(() => { this.isSubmitting = false; });
      },
      removeConsiderationAfterTreatment(afterTreatment) {
        this.model.considerationsAfterTreatment.splice(this.model.considerationsAfterTreatment.indexOf(afterTreatment), 1);
      },
      reorderConsiderationsAfterTreatment(afterTreatment, direction) {
        if (direction == '+') {
          let upper = this.model.considerationsAfterTreatment.find(s => s.displayOrder == afterTreatment.displayOrder - 1);
          if (upper != null)
            upper.displayOrder++;
          afterTreatment.displayOrder--;
        }
        else {
          let bottom = this.model.considerationsAfterTreatment.find(s => s.displayOrder == afterTreatment.displayOrder + 1);
          if (bottom != null)
            bottom.displayOrder--;
          afterTreatment.displayOrder++;
        }

        this.model.considerationsAfterTreatment.sort((a, b) => parseFloat(a.displayOrder) - parseFloat(b.displayOrder));
      },
      addConsiderationAfterTreatment() {
        this.model.considerationsAfterTreatment.push({ id: '', customConsentFormID: this.model.id, text: '', displayOrder: this.model.considerationsAfterTreatment.length + 1 });
      },
      removeSideEffect(sideEffect) {
        this.model.sideEffects.splice(this.model.sideEffects.indexOf(sideEffect), 1);
      },
      addConsiderationBeforeTreatment() {
        this.model.considerationsBeforeTreatment.push({ id: '', customConsentFormID: this.model.id, text: '', displayOrder: this.model.considerationsBeforeTreatment.length + 1 });
      },
      getLanguagesList() {
        commonService.getLanguagesList().then((response) => {
          this.cultures = response.filter(s => s.culture == 'en-US' || s.culture == 'tr-TR');
        });
      },
      addSideEffect() {
        this.model.sideEffects.push({ id: '', customConsentFormID: this.model.id, text: '', displayOrder: this.model.sideEffects.length + 1 });
      },
      reorderConsiderationsBeforeTreatment(beforeTreatment, direction) {
        if (direction == '+') {
          let upper = this.model.considerationsBeforeTreatment.find(s => s.displayOrder == beforeTreatment.displayOrder - 1);
          if (upper != null)
            upper.displayOrder++;
          beforeTreatment.displayOrder--;
        }
        else {
          let bottom = this.model.considerationsBeforeTreatment.find(s => s.displayOrder == beforeTreatment.displayOrder + 1);
          if (bottom != null)
            bottom.displayOrder--;
          beforeTreatment.displayOrder++;
        }

        this.model.considerationsBeforeTreatment.sort((a, b) => parseFloat(a.displayOrder) - parseFloat(b.displayOrder));
      },
      removeConsiderationBeforeTreatment(beforeTreatment) {
        this.model.considerationsBeforeTreatment.splice(this.model.considerationsBeforeTreatment.indexOf(beforeTreatment), 1);
      },
      addClause() {
        this.model.clauses.push({ id: '', customConsentFormID: this.model.id, text: '', displayOrder: this.model.clauses.length + 1, isBlockerQuestion: false, blockerAnswerType: null, detailAnswerType: null, detailLabel: '' });
      },
      reorderClauses(clause, direction) {
        if (direction == '+') {
          let upper = this.model.clauses.find(s => s.displayOrder == clause.displayOrder - 1);
          if (upper != null)
            upper.displayOrder++;
          clause.displayOrder--;
        }
        else {
          let bottom = this.model.clauses.find(s => s.displayOrder == clause.displayOrder + 1);
          if (bottom != null)
            bottom.displayOrder--;
          clause.displayOrder++;
        }

        this.model.clauses.sort((a, b) => parseFloat(a.displayOrder) - parseFloat(b.displayOrder));
      },
      removeClause(clause) {
        this.model.clauses.splice(this.model.clauses.indexOf(clause), 1);
      },
      reorderSideEffects(sideEffect, direction) {
        if (direction == '+') {
          let upper = this.model.sideEffects.find(s => s.displayOrder == sideEffect.displayOrder - 1);
          if (upper != null)
            upper.displayOrder++;
          sideEffect.displayOrder--;
        }
        else {
          let bottom = this.model.sideEffects.find(s => s.displayOrder == sideEffect.displayOrder + 1);
          if (bottom != null)
            bottom.displayOrder--;
          sideEffect.displayOrder++;
        }

        this.model.sideEffects.sort((a, b) => parseFloat(a.displayOrder) - parseFloat(b.displayOrder));
      },
    },
    computed: {
      /*
       * Seçenek listeleri COMPUTED: çeviriler i18n.js'te HubApi'den ASENKRON geliyor.
       * `mounted`'da `$t()` çağırıp sonucu data'ya yazmak, çeviriler henüz gelmemişken
       * ham anahtarı ("YesToAll") kalıcı olarak seçeneğe yazıyordu. Computed, mesajlar
       * yüklendiğinde kendini yeniden hesaplar.
       */
      consentTypeList() {
        return [
          { id: -1, name: this.$t('NoToAll') },
          { id: 1, name: this.$t('YesToAll') }
        ];
      },
      blockerAnswerTypeList() {
        return [
          { id: 1, name: this.$t('YesAnswerBlocks') },
          { id: -1, name: this.$t('NoAnswerBlocks') }
        ];
      },
      detailAnswerTypeList() {
        return [
          { id: 1, name: this.$t('DetailRequiredOnYes') },
          { id: -1, name: this.$t('DetailRequiredOnNo') }
        ];
      },
      // Dönüştürme yalnız YENİ formda: kayıtlı bir formun içeriğini AI ile ezmek,
      // düzenlemeye gelen personelin beklediği şey değil.
      canUseAiConvert() {
        return !this.model.id && this.aiConvertAvailable;
      },
      // Yönlendirme dönüştürmeye kadar veya kullanıcı kapatana kadar durur. Doluluğa
      // bağlamak ilk harfte kartın zıplayarak kaybolmasına yol açıyor.
      showAiConvertPrompt() {
        return this.canUseAiConvert && !this.aiReview && !this.aiPromptDismissed;
      },
      requiresAiAcknowledgment() {
        return this.model.aiGenerated === true && !this.model.id;
      },
      // Dönüştürme mevcut alanları ezeceği için modalda uyarı gösterilir.
      hasExistingFormData() {
        return !!(this.model.formName
          || this.model.treatmentName
          || this.model.descriptionHtml
          || this.model.consentText
          || this.model.clauses?.length
          || this.model.sideEffects?.length
          || this.model.considerationsBeforeTreatment?.length
          || this.model.considerationsAfterTreatment?.length);
      },
      confidenceLabel() {
        const percent = Math.round((this.aiReview?.confidence || 0) * 100);
        return this.$t('AiConvertReviewConfidence').replace('{percent}', percent);
      },
      confidenceVariant() {
        const confidence = this.aiReview?.confidence || 0;

        if (confidence >= 0.8) return 'success';
        if (confidence >= 0.5) return 'warning';
        return 'danger';
      },
      /**
       * Sadakat uyarısı yalnız gerçekten zayıf dayanak varsa çıkar. Her dönüştürmede
       * "%100 dayanaklı" yazmak bandı gürültüye çevirir ve asıl uyarıları gölgeler.
       */
      showGroundingWarning() {
        return (this.aiReview?.validation?.ungroundedItemCount || 0) > 0;
      },
      groundingItems() {
        if (!this.showGroundingWarning) return [];

        const sectionKeys = {
          clause: 'Clauses',
          sideEffect: 'SideEffects',
          considerationBefore: 'ConsiderationsBeforeTreatment',
          considerationAfter: 'ConsiderationsAfterTreatment'
        };

        return (this.aiReview.validation.ungroundedItems || []).map(item => ({
          text: item.text,
          sectionKey: sectionKeys[item.section] || ''
        }));
      },
      hasBlockerQuestions() {
        return this.model.clauses && this.model.clauses.some(clause => clause.isBlockerQuestion === true);
      },
      blockerMessageState() {
        if (!this.hasBlockerQuestions) return null;
        if (!this.model.blockerMessage || this.model.blockerMessage.trim() === '') return false;
        return null;
      }
    },
    watch: {
      'model.showClauses': function (val) {
        if (val == true && !this.model.id && this.model.clauses?.length == 0) {
          this.model.clauses.push({ customConsentFormID: this.model.id, text: '', displayOrder: 1, isBlockerQuestion: false, blockerAnswerType: null, detailAnswerType: null, detailLabel: '' });
        }

        if (val == false) {
          this.model.clauses = [];
          this.model.clauseConsentType = null;
        }
      },
      'model.showSideEffects': function (val) {
        if (val == true && !this.model.id && this.model.sideEffects?.length == 0) {
          this.model.sideEffects.push({ customConsentFormID: this.model.id, text: '', displayOrder: 1 });
        }

        if (val == false) {
          this.model.sideEffects = [];
        }
      },
      'model.showConsiderationsBeforeTreatment': function (val) {
        if (val == true && !this.model.id && this.model.considerationsBeforeTreatment?.length == 0) {
          this.model.considerationsBeforeTreatment.push({ customConsentFormID: this.model.id, text: '', displayOrder: 1 });
        }

        if (val == false) {
          this.model.considerationsBeforeTreatment = [];
        }
      },
      'model.showConsiderationsAfterTreatment': function (val) {
        if (val == true && !this.model.id && this.model.considerationsAfterTreatment?.length == 0) {
          this.model.considerationsAfterTreatment.push({ customConsentFormID: this.model.id, text: '', displayOrder: 1 });
        }

        if (val == false) {
          this.model.considerationsAfterTreatment = [];
        }
      }
    },
    mounted() {
      if (this.customConsentFormId) {
        this.model.id = this.customConsentFormId;
        this.getCustomConsentForm();
      }

      this.getLanguagesList();

      if (!this.customConsentFormId) {
        this.getAiConvertAvailability();
      }
    }
  }
</script>

<style scoped>
  .ai-prompt {
    cursor: pointer;
    border: 1px dashed #d7dbe0;
  }

  .ai-prompt__icon {
    color: var(--iq-primary);
  }

  .close-icon {
    cursor: pointer;
    opacity: .55;
  }

  .close-icon:hover {
    opacity: 1;
  }

  /* Lite temasında .alert `display:flex; flex-direction:row`; bandın üç bloğu
     (başlık, uyarı listesi, token satırı) yan yana dizilip üst üste biniyordu. */
  .ai-review {
    flex-direction: column;
  }

  .ai-review__items {
    padding-left: 18px;
    margin-bottom: 0;
  }

  /* Sıralama okları + sil tek satırda, metin alanıyla aynı hizada. Önceden
     <br /> ve &nbsp; dolgularıyla dağılmış ve dikeyde kaymış duruyordu. */
  .ccf-controls {
    display: flex;
    align-items: center;
    gap: .5rem;
  }

  .ccf-controls__move .btn {
    padding: 0 .45rem;
    line-height: 1.35;
  }

  .ccf-controls__remove {
    color: #dc3545;
  }

  .ccf-controls__remove:hover {
    background: #fdecee;
  }
</style>
